/**
 * Колонки TanStack таблицы планов и увеличение фото товара. Вынесено из
 * `components/PlansTable.tsx` (нарезка 14.10.26).
 */

import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { DownloadLink } from "@/components/DownloadLink";
import { Фото, type Увеличение } from "@/components/PhotoZoom";
import { SupplierName } from "@/components/SupplierName";
import { nf } from "@/lib/format";
import { cardMark, supplierName, techcardHref } from "@/lib/plans";
import type { PlanRow } from "@/lib/providers";
import { Badge } from "./Badge";
import { fmtDate, fmtMoney } from "./format";
import { MARK_TONE, PRODUCE_TONE, STATUS_TONE } from "./style";

const column = createColumnHelper<PlanRow>();

export function usePlanColumns() {
  // Наведение на фото живёт ЗДЕСЬ, а не в строке: виртуализатор
  // переиспользует узлы строк (правило `PhotoZoom`).
  const [увеличено, setУвеличено] = useState<Увеличение | null>(null);

  // Определения колонок мемоизируются НЕ ради красоты: пересозданный массив
  // заставляет TanStack Table пересчитать таблицу целиком на каждый рендер —
  // это причина номер один жалоб «почему таблица тормозит».
  const columns = useMemo(
    () => [
      column.accessor("id", {
        id: "plan",
        header: "партия",
        // Код партии (`1724-2026-09-15`) с экрана снят 15.09.26: это номер
        // поставщика и дата создания, обе уже стоят в строке и в группе.
        cell: (info) => <span className="tnum text-accent-text">{info.getValue()}</span>,
      }),
      column.accessor("plan_date", {
        id: "date",
        header: "дата",
        cell: (info) => (
          <span className="tnum">{fmtDate(info.getValue())}</span>
        ),
      }),
      column.accessor("product_name", {
        id: "product",
        header: "товар",
        // Название и артикул ценности в строке не несут (замечание владельца
        // 15.09.26) и живут в подсказке фото; поиск по ним в тулбаре остался.
        cell: (info) => {
          const row = info.row.original;
          const подсказка = [row.product_name, row.product_article]
            .filter(Boolean)
            .join(" · ");
          return (
            <Фото
              src={row.product_image}
              title={подсказка || undefined}
              size="h-[30px] w-[23px]"
              onShow={setУвеличено}
            />
          );
        },
      }),
      column.accessor("supplier_short", {
        id: "supplier",
        header: "поставщик",
        cell: (info) => (
          <span className="block truncate">
            <SupplierName id={info.row.original.supplier_id} name={supplierName(info.row.original)} />
          </span>
        ),
      }),
      column.accessor("line_count", {
        id: "lines",
        header: "строк",
        cell: (info) => <span className="tnum">{info.getValue()}</span>,
      }),
      column.accessor("qty_planned", {
        id: "planned",
        header: "план",
        cell: (info) => (
          <span className="tnum">{nf.format(info.getValue())}</span>
        ),
      }),
      column.accessor("qty_ordered", {
        id: "ordered",
        header: "в заказе",
        cell: (info) => {
          const row = info.row.original;
          const done =
            row.qty_planned > 0 && info.getValue() >= row.qty_planned;
          return (
            <span className={done ? "tnum text-ok" : "tnum"}>
              {nf.format(info.getValue())}
            </span>
          );
        },
      }),
      column.accessor("amount", {
        id: "amount",
        header: "сумма ¥",
        cell: (info) => {
          const missing = info.row.original.no_price_lines;
          return (
            <span className="inline-flex items-center justify-end gap-1">
              {/* «Нет цены» — не косметика: партия без цены не уедет в заказ,
                  и увидеть её надо ДО того, как настанет дата отгрузки. */}
              {missing > 0 ? (
                <span
                  title={`строк без цены: ${missing}`}
                  className="rounded border border-err/30 bg-err-bg px-1 text-[10.5px] font-medium text-err"
                >
                  {missing}
                </span>
              ) : null}
              <span className="tnum">{fmtMoney(info.getValue())}</span>
            </span>
          );
        },
      }),
      // Техкарта стоит рядом с деньгами, а не в конце строки: решение «отдавать
      // партию фабрике» читается слева направо — сколько, на сколько, есть ли
      // карта. Оплаты в строке нет (разметка 25.09.26 15:28 п.1): платёж висит на
      // QR-папке, и его метка — на заголовке группы.
      column.accessor("has_card", {
        id: "card",
        header: "карта",
        cell: (info) => {
          const mark = cardMark(info.row.original);
          const badge = <Badge tone={MARK_TONE[mark.tone]} text={mark.text} title={mark.title} />;
          if (!info.getValue()) return badge;
          // «Есть» — архив техкарты своей ручкой (п.3). Клик не сворачивает строку.
          return (
            <DownloadLink
              href={techcardHref({ ids: [info.row.original.id] })}
              data-annot={`техкарта ${info.row.original.id}`}
              className="hover:underline"
            >
              {badge}
            </DownloadLink>
          );
        },
      }),
      column.accessor("status_name", {
        id: "status",
        header: "статус",
        cell: (info) => (
          <Badge
            tone={STATUS_TONE[info.row.original.status_id]}
            text={info.getValue()}
          />
        ),
      }),
      column.accessor("produce_status_name", {
        id: "produce",
        header: "производство",
        cell: (info) => (
          <Badge
            tone={PRODUCE_TONE[info.row.original.produce_status_id]}
            text={info.getValue()}
          />
        ),
      }),
    ],
    [],
  );

  return { columns, увеличено };
}
